import { useAtomValue, useSetAtom } from 'jotai';
import { type ReactElement, useMemo } from 'react';
import { IconCircleHalfStrokeSolid, IconCircleSolid, IconSunBright } from '@optiaxiom/icons';

import { previewThemeAtom, type Theme, themeAtom } from '@frontend/atoms';

import { type TActionInput, type TModePath } from '../store';

import { useNavigationActions } from './useNavigationActions';

/**
 * Build the globally-registered Spotlight actions, grouped by mode path: the root
 * (`/`) commands — "Change theme" plus the gated navigation actions — and the theme
 * controls under `/set-theme`. Memoized on the committed theme and the navigation set.
 */
export function useGlobalActions(): Record<TModePath, TActionInput[]> {
  const currentTheme = useAtomValue(themeAtom);
  const setTheme = useSetAtom(themeAtom);
  const setPreview = useSetAtom(previewThemeAtom);
  const navigationActions = useNavigationActions();

  return useMemo(() => {
    const themeAction = (
      key: string,
      title: string,
      icon: ReactElement,
      theme: Theme
    ): TActionInput => ({
      key,
      type: 'theme',
      group: 'theme',
      label: title,
      // Focus the already-active theme first so entering the mode doesn't flash a preview.
      priority: theme === currentTheme ? 1 : 0,
      data: { title, icon },
      onFocus: () => setPreview(theme),
      onBlur: () => setPreview(null),
      execute: (_ctx, spotlight) => {
        setTheme(theme);
        spotlight.close();
      },
    });

    return {
      '/': [
        {
          key: 'change-theme',
          type: 'setting',
          group: 'settings',
          label: 'Change theme',
          keywords: ['appearance', 'dark', 'light', 'color'],
          data: { title: 'Change theme', icon: <IconCircleHalfStrokeSolid /> },
          execute: (_ctx, spotlight) => {
            spotlight.pushMode('/set-theme');
            return true;
          },
        },
        ...navigationActions,
      ],
      '/set-theme': [
        themeAction('dark', 'Dark', <IconCircleSolid />, 'dark'),
        themeAction('light', 'Light', <IconSunBright />, 'light'),
      ],
    };
  }, [currentTheme, navigationActions, setPreview, setTheme]);
}
